import { useCallback, useState } from "react";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { processError } from "../../../../common/utils/base_error";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { CentroCustoDropDown } from "../../../../common/domain/models/centro_custo_dropdown";
import { useDropDownLoader } from "../../../../common/hooks/use_dropdown_loader";
import { useNavigate } from "react-router";
import { useMedicaoFilters } from "../hooks/medicao_terceirizacao/filter";
import { useMedicaoPagination } from "../hooks/medicao_terceirizacao/pagination";
import { MedicaoTerceirizacaoLista } from "../../domain/models/medicao_terceirizacao_lista";

type LoadMedicaoFuncState = {
  IdMedicaoTerceirizacao?: number;
  page?: number;
  rowsPerPage?: number;
  centroDeCusto?: number[],
  centroDeCustoLista?: CentroCustoDropDown[] | undefined,
  FornecedorPesquisa?: number,
  ContratoPesquisa?: number,
  NMedicaoPesquisa?: string, 
  FornecedorAvaliadoPesquisa?: number, 
  NotaFiscalPesquisa?: number, 
};

export const useAvaliacaoFornecimentoListaViewModel = () => {
  const {
    commons: {  },
    terceirizacao: {getAllListagemMedicaoEntregaUseCase},
  } = useDependencyInjection();
  const debouncedRef = useCreateDebounceHandler(750);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [openLegendaModal, setOpenLegendaModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [AvaliacaoLista, setAvaliacaoLista] = useState<MedicaoTerceirizacaoLista[]>([]);
  const [selectedAvaliacao, setSelectedAvaliacao] = useState<MedicaoTerceirizacaoLista>();
  const {pushErrorNotification } = usePageLoader();
  
  const filters = useMedicaoFilters();
  const pagination = useMedicaoPagination();
  const navigate = useNavigate();

  const {
    loadCentroCustoDropDown,
  } = useDropDownLoader();

  // #region Funções de carregamento
  const loadMedicaoListaState = useCallback(async (funcState?: LoadMedicaoFuncState) => {
      try {
        const filterID = evaluateParamValue(funcState?.IdMedicaoTerceirizacao, filters.IdMedicaoTerceirizacao);
        const filterFornecedor = funcState?.FornecedorPesquisa ?? filters.FornecedorPesquisa;
        const filterContrato = funcState?.ContratoPesquisa ?? filters.ContratoPesquisa;
        const filterFornecedorAvaliadoPesquisa = funcState?.FornecedorAvaliadoPesquisa ?? filters.FornecedorAvaliadoPesquisa;
        const filterNMedicaoPesquisa = funcState?.NMedicaoPesquisa ?? filters.NMedicaoPesquisa;
        const filterCentroDeCusto = funcState?.centroDeCusto ?? filters.centroDeCusto;
        const filterNotaFiscal = funcState?.NotaFiscalPesquisa ?? filters.NotaFiscalPesquisa;
        const page = funcState?.page ?? pagination.page;
        const rows = funcState?.rowsPerPage ?? pagination.rowsPerPage;
  
        await getAllListagemMedicaoEntregaUseCase.getAllListagemMedicaoEntrega({
            IdMedicaoTerceirizacao: filterID,
            centroDeCusto: filterCentroDeCusto,
            FornecedorPesquisa: filterFornecedor,
            NMedicaoPesquisa: filterNMedicaoPesquisa,
            FornecedorAvaliadoPesquisa: filterFornecedorAvaliadoPesquisa,
            ContratoPesquisa: filterContrato,
            NotaFiscalPesquisa: filterNotaFiscal,
          },
          page,
          rows
        );
        setAvaliacaoLista(getAllListagemMedicaoEntregaUseCase.items);
        pagination.setRowsPerPage(getAllListagemMedicaoEntregaUseCase.pageSize);
        pagination.setTotalItems(getAllListagemMedicaoEntregaUseCase.totalItems);
      } catch (e: unknown) {
          throw new Error("Falha ao buscar Avaliacao Fornecimento!");
      }
    }, [filters.ContratoPesquisa, filters.FornecedorAvaliadoPesquisa, filters.FornecedorPesquisa, filters.IdMedicaoTerceirizacao, filters.NMedicaoPesquisa, filters.NotaFiscalPesquisa, filters.centroDeCusto, getAllListagemMedicaoEntregaUseCase, pagination]);
  
  //#endregion

  // #region useEffect
  
  usePageStartBootstrap(async () => {
    try {
      await loadMedicaoListaState();
      await loadCentroCustoDropDown(list => useMedicaoFilters.setState({ centroDeCustoLista: list }));
    } catch (e: unknown) {
      setErrorMessage(processError(e, 'Falha ao buscar dados da pagina!')[0]);
    }
  });

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);
      await loadMedicaoListaState();
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [loadMedicaoListaState, pushErrorNotification]);
  //#endregion
  
  // #region Eventos
  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      pagination.setPage(page + 1);
      await loadMedicaoListaState({page: page + 1,});
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [loadMedicaoListaState, pagination, pushErrorNotification]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
        setLoading(true);
        pagination.setPage(1);
        pagination.setRowsPerPage(rowsPerPage);
        await loadMedicaoListaState({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [loadMedicaoListaState, pagination, pushErrorNotification]);

  const handleCadastroMedicaoOpenState = useCallback((IdMedicaoTerceirizacao: number | null, openState: boolean) => {
    if (openState) {
      if (IdMedicaoTerceirizacao) {
        navigate(`/MedicaoTerceirizacao_avaliacafornecimento/${IdMedicaoTerceirizacao}`)
      } 
    }
  }, [navigate]);

  //#endregion


  //#region Filtros
  const handleClearFilters = useCallback(async () => {
    try {
        setLoading(true);
        filters.clearFilters();
        pagination.setPage(1);
        await loadMedicaoListaState();
    } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar Avaliacao Fornecimento!'));
    } finally {
        setLoading(false);
    }
  }, [filters, loadMedicaoListaState, pagination, pushErrorNotification]);

  const handleFornecedorChange = useDebouncedCallback(debouncedRef, async (newValue: number) => {
    useMedicaoFilters.getState().setFornecedorPesquisa(newValue);

    try {
      setLoading(true);
      useMedicaoPagination.getState().setPage(1);
      await loadMedicaoListaState({ FornecedorPesquisa: newValue, page: 1 } as any);
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar Avaliacao Fornecimento!"));
    } finally {
      setLoading(false);
    }

    return () => {};
  }, [pushErrorNotification, loadMedicaoListaState]);

  const handleContratoChange = useDebouncedCallback(debouncedRef, async (newValue: number) => {
    useMedicaoFilters.getState().setContratoPesquisa(newValue);

    try {
      setLoading(true);
      useMedicaoPagination.getState().setPage(1);
      await loadMedicaoListaState({ ContratoPesquisa: newValue, page: 1 } as any);
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar Avaliacao Fornecimento!"));
    } finally {
      setLoading(false);
    }

    return () => {};
  }, [pushErrorNotification, loadMedicaoListaState]);

  const handleCentroDeCustoChange = useDebouncedCallback(debouncedRef, (newValue: number[]) => {
    useMedicaoFilters.getState().setCentroDeCusto(newValue);
    return async () => {
      try {
        setLoading(true);
        useMedicaoPagination.getState().setPage(1);
        await loadMedicaoListaState({ centroDeCusto: newValue, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao carregar Avaliacao Fornecimento!"));
      } finally {
        setLoading(false);
      }
    }
  }, [ loadMedicaoListaState, pushErrorNotification, ]);


  const handleNMedicaoCampoChange = useDebouncedCallback(debouncedRef, (newValue: string) => {
    useMedicaoFilters.getState().setNMedicaoPesquisa(newValue);
    return async () => {
      try {
        setLoading(true);
        useMedicaoPagination.getState().setPage(1);
        await loadMedicaoListaState({ NMedicaoPesquisa: newValue, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao carregar Avaliação de Fornecimento!"));
      } finally {
        setLoading(false);
      }
    }
  }, [ loadMedicaoListaState, pushErrorNotification,]);

  const handleFornecedorAvaliadoPesquisaChange = useDebouncedCallback(debouncedRef, (newValue: number) => {
    useMedicaoFilters.getState().setFornecedorAvaliadoPesquisa(newValue);
    return async () => {
      try {
        setLoading(true);
        useMedicaoPagination.getState().setPage(1);
        await loadMedicaoListaState({ FornecedorAvaliadoPesquisa: newValue, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao carregar Avaliação de Fornecimento!"));
      } finally {
        setLoading(false);
      }
    }
  }, [ loadMedicaoListaState, pushErrorNotification,]);

  const handleNotaFiscalPesquisa = useDebouncedCallback(debouncedRef, (newValue: number) => {
    useMedicaoFilters.getState().setNotaFiscalPesquisa(newValue);
    return async () => {
      try {
        setLoading(true);
        useMedicaoPagination.getState().setPage(1);
        await loadMedicaoListaState({ NotaFiscalPesquisa: newValue, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao carregar Avaliação de Fornecimento!"));
      } finally {
        setLoading(false);
      }
    }
  }, [ loadMedicaoListaState, pushErrorNotification,]);
  //#endregion

  
  const handleShowLegenda = useCallback((openState: boolean) => {
      setOpenLegendaModal(openState);
  }, []);
  
  
  
  return {
    loading,
    errorMessage,
    saving,
    pagination,
    filters,
    AvaliacaoLista,
    selectedAvaliacao,
    openLegendaModal,
    handlePageChange,
    handleRowsPerPageChange,
    handleReload,
    handleClearFilters,
    handleFornecedorChange,
    handleCentroDeCustoChange,
    handleNMedicaoCampoChange,
    handleFornecedorAvaliadoPesquisaChange,
    handleCadastroMedicaoOpenState,
    handleContratoChange,
    handleShowLegenda,
    handleNotaFiscalPesquisa,
  };
};
